import React, { useEffect, useState } from "react";
import { useNavigate, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { config } from "../config/config";
import ManagerKPI from "../components/ManagerKPI";
import CryptoJS from "crypto-js";
import { 
  LayoutDashboard, 
  User, 
  Lock, 
  LogOut, 
  Menu, 
  X, 
  Calendar, 
  Clock, 
  AlertCircle, 
  CheckCircle2, 
  Info,
  ChevronRight,
  TrendingUp,
  Sun,
  Moon
} from "lucide-react";

import PerformanceSubPage from "./PerformanceSubPage";
import ProfileSubPage from "./ProfileSubPage";
import SecuritySubPage from "./SecuritySubPage";

const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

// Helper to format ISO Date to human-readable format
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (e) {
    return dateString;
  }
};

// Helper to format ISO Date to human-readable time
const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  } catch (e) {
    return "";
  }
};

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [managerData, setManagerData] = useState(null);
  const [managerId, setManagerId] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    accountNumber: "",
    bank: "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPasscode, setShowCurrentPasscode] = useState(false);
  const [showNewPasscode, setShowNewPasscode] = useState(false);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("manager-theme") || "light";
  });

  const toggleTheme = () => {
    const nextTheme = theme === "light" ? "dark" : "light";
    setTheme(nextTheme);
    localStorage.setItem("manager-theme", nextTheme);
  };

  const getSwalConfig = () => ({
    confirmButtonColor: '#0ea5e9',
    background: theme === "dark" ? '#0B0F19' : '#ffffff',
    color: theme === "dark" ? '#ffffff' : '#0f172a',
  });

  useEffect(() => {
    const encryptedManager = localStorage.getItem("manager");
    let isManagerLoggedIn = false;
    let mId = null;
    
    if (encryptedManager) {
      try {
        const bytes = CryptoJS.AES.decrypt(encryptedManager, SECRET_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        if (decrypted) {
          const parsed = JSON.parse(decrypted);
          if (parsed) {
            isManagerLoggedIn = true;
            const mData = parsed.manager || parsed;
            mId = mData._id || mData.id;
          }
        }
      } catch (e) {
        isManagerLoggedIn = false;
      }
    }

    if (!isManagerLoggedIn || !mId) {
      navigate("/manager-login");
      return;
    }

    setManagerId(mId);

    const fetchDashboardData = async () => {
      try {
        const [profileRes, perfRes] = await Promise.all([
          axios.get(`${config.apiBaseUrl}${config.endpoints.managerProfile}/${mId}`, {
            withCredentials: true,
          }),
          axios.get(`${config.apiBaseUrl}${config.endpoints.managerPerformance}/${mId}`, {
            withCredentials: true,
          }).catch(() => ({ data: null }))
        ]);
        
        const data = profileRes.data;
        const md = data.manager || data;
        setManagerData(md);

        setFormData({
          fullName: md.fullName || "",
          email: md.email || "",
          phone: md.phone || "",
          accountNumber: md.accountNumber || "",
          bank: md.bank || "",
        });

        if (perfRes.data) {
          setKpiData(perfRes.data.performance || perfRes.data.kpi || perfRes.data);
        } else if (data.kpi) {
          setKpiData(data.kpi);
        }
      } catch (error) {
        localStorage.removeItem("manager");
        navigate("/manager-login");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("manager");
    navigate("/manager-login");
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!managerId) return;
    setUpdating(true);
    try {
      const response = await axios.put(`${config.apiBaseUrl}${config.endpoints.managerProfile}/${managerId}`, formData, {
        withCredentials: true,
      });
      Swal.fire({
        ...getSwalConfig(),
        icon: 'success',
        title: 'Success!',
        text: 'Profile updated successfully!',
      });
      if (response.data && response.data.manager) {
        setManagerData(response.data.manager);
      }
    } catch (error) {
      Swal.fire({
        ...getSwalConfig(),
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || "Failed to update profile.",
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!managerId) return;
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      Swal.fire({ ...getSwalConfig(), icon: 'warning', text: 'Please fill all password fields.' });
      return;
    }
    if (passwordData.newPassword.length !== 6) {
      Swal.fire({ ...getSwalConfig(), icon: 'warning', text: 'New passcode must be exactly 6 digits.' });
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(`${config.apiBaseUrl}${config.endpoints.managerChangePassword}/${managerId}`, passwordData, {
        withCredentials: true,
      });
      Swal.fire({
        ...getSwalConfig(),
        icon: 'success',
        title: 'Success!',
        text: 'Passcode changed successfully!',
      });
      setPasswordData({ currentPassword: "", newPassword: "" });
    } catch (error) {
      Swal.fire({
        ...getSwalConfig(),
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || "Failed to change passcode.",
      });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${theme === 'dark' ? 'bg-slate-950' : 'bg-slate-50'}`}>
        <div className="flex flex-col items-center gap-3">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 rounded-full border-4 border-sky-500/20"></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-sky-500 animate-spin"></div>
          </div>
          <p className="text-slate-500 dark:text-slate-400 text-sm font-semibold tracking-wider animate-pulse">Loading Manager Panel...</p>
        </div>
      </div>
    );
  }

  if (!managerData) return null;

  const navItems = [
    {
      name: "Performance",
      path: "/manager-dashboard/performance",
      icon: LayoutDashboard,
      description: "KPI metrics and goals"
    },
    {
      name: "Profile Settings",
      path: "/manager-dashboard/profile",
      icon: User,
      description: "Manage personal details"
    },
    {
      name: "Change Passcode",
      path: "/manager-dashboard/security",
      icon: Lock,
      description: "Update account security"
    }
  ];

  // Helper to determine if a route is currently active
  const isTabActive = (itemPath) => {
    return location.pathname === itemPath || (location.pathname === "/manager-dashboard" && itemPath.endsWith("performance"));
  };

  // Get current page title for top bar
  const getCurrentPageTitle = () => {
    const activeItem = navItems.find(item => isTabActive(item.path));
    return activeItem ? activeItem.name : "Manager Dashboard";
  };

  // Sidebar Layout
  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-[#0B0F19] text-slate-600 dark:text-slate-300 select-none">
      {/* Header Brand */}
      <div className="flex items-center gap-2 px-4 py-4 md:px-6 md:py-6 border-b border-slate-200 dark:border-slate-800/80">
        <div className="w-9 h-9 md:w-10 md:h-10 bg-gradient-to-br from-sky-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/10">
          <span className="text-white font-black text-lg">{managerData.fullName.charAt(0).toUpperCase()}</span>
        </div>
        <div>
          <h2 className="text-slate-800 dark:text-white font-extrabold text-sm tracking-tight leading-none mb-1">Task Manager</h2>
          <p className="text-slate-400 dark:text-slate-500 text-[10px] uppercase tracking-widest font-black leading-none">Dashboard</p>
        </div>
      </div>

      {/* User Info Details Summary */}
      <div className="px-3 py-2 mx-2 my-3 bg-slate-200/50 dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800/50 md:px-5 md:py-4 md:mx-3 md:my-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-300 dark:bg-slate-800 flex items-center justify-center border border-slate-200 dark:border-slate-700 text-sky-600 dark:text-sky-400 font-bold text-xs uppercase">
            {managerData.fullName.split(" ").map(n => n[0]).join("").substring(0, 2)}
          </div>
          <div className="overflow-hidden">
            <h4 className="text-xs font-bold text-slate-800 dark:text-white truncate leading-tight">{managerData.fullName}</h4>
            <p className="text-[10px] text-slate-500 dark:text-slate-500 truncate mt-0.5">{managerData.email}</p>
          </div>
        </div>
      </div>

      {/* Nav Menu */}
      <nav className="flex-1 px-2 md:px-4 space-y-1 md:space-y-1.5 overflow-y-auto custom-scrollbar">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isTabActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-3 rounded-xl transition-all duration-200 group border ${
                active 
                  ? "bg-sky-500/10 dark:bg-gradient-to-r dark:from-sky-500/10 dark:to-indigo-500/10 border-sky-500/20 text-sky-600 dark:text-sky-400 font-semibold" 
                  : "border-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-900 hover:text-slate-800 dark:hover:text-slate-200"
              }`}
            >
              <Icon className={`w-5 h-5 transition-transform group-hover:scale-105 ${active ? "text-sky-600 dark:text-sky-400" : "text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-400"}`} />
              <div className="flex-1">
                <p className="text-xs leading-none">{item.name}</p>
                <p className="text-[9px] text-slate-400 dark:text-slate-500 leading-none mt-1 group-hover:text-slate-600 dark:group-hover:text-slate-400 truncate">{item.description}</p>
              </div>
              {active && <ChevronRight className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400 animate-pulse" />}
            </Link>
          );
        })}
      </nav>

      {/* Footer / Logout Button */}
      <div className="p-3 md:p-4 border-t border-slate-200 dark:border-slate-800/80">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 text-xs font-bold text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300"
        >
          <LogOut className="w-4 h-4 text-slate-400 dark:text-slate-500" />
          Logout Account
        </button>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark bg-slate-950 text-white' : 'bg-slate-50 text-slate-800'} flex flex-col md:flex-row overflow-hidden`}>
      {/* 1. Sidebar - Desktop View */}
      <aside className="hidden md:block w-72 h-screen border-r border-slate-200 dark:border-slate-900 flex-shrink-0 sticky top-0">
        <SidebarContent />
      </aside>

      {/* 2. Sidebar - Mobile View Drawer */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          {/* Backdrop overlay */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300" 
            onClick={() => setSidebarOpen(false)}
          />
          {/* Menu Drawer */}
          <div className="relative flex flex-col w-64 md:w-72 max-w-xs h-full bg-slate-50 dark:bg-[#0B0F19] border-r border-slate-200 dark:border-slate-900 transform transition-transform duration-300 ease-in-out shadow-2xl">
            {/* Close Button Inside Drawer */}
            <button 
              onClick={() => setSidebarOpen(false)}
              className="absolute right-4 top-5 p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* 3. Main Dashboard Wrapper */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-y-auto">
        {/* Top Navbar */}
        <header className="bg-white dark:bg-[#0B0F19] border-b border-slate-200 dark:border-slate-900/60 px-3 py-3 md:px-6 md:py-4 flex items-center justify-between sticky top-0 z-40 backdrop-blur-md bg-opacity-95 text-slate-800 dark:text-white">
          <div className="flex items-center gap-3">
            {/* Hamburger for Mobile screen */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 -ml-2 rounded-lg border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div>
              <h1 className="text-slate-800 dark:text-white font-extrabold text-sm md:text-base leading-none tracking-tight">{getCurrentPageTitle()}</h1>
              <p className="text-[10px] text-slate-400 dark:text-slate-500 hidden sm:block mt-1 font-semibold uppercase tracking-wider">Manager Workspace</p>
            </div>
          </div>

          {/* Quick Header Widget */}
          <div className="flex items-center gap-4">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors duration-250 flex items-center justify-center"
              title={`Switch to ${theme === "light" ? "Dark" : "Light"} Mode`}
            >
              {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>

            <div className="hidden sm:flex flex-col text-right">
              <span className="text-xs font-bold text-slate-800 dark:text-white leading-none">{managerData.fullName}</span>
              <span className="text-[9px] text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-widest mt-1 flex items-center justify-end gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Active Manager
              </span>
            </div>
            <div className="w-8 h-8 md:w-9 md:h-9 bg-gradient-to-tr from-sky-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/10">
              <span className="text-white font-black text-xs">{managerData.fullName.charAt(0).toUpperCase()}</span>
            </div>
          </div>
        </header>

        {/* 4. Page Subroutes Content */}
        <main className="flex-grow p-3 sm:p-6 md:p-8 max-w-5xl w-full mx-auto pb-16">
          <Routes>
            <Route path="/" element={<Navigate to="performance" replace />} />
            <Route 
              path="performance" 
              element={
                <PerformanceSubPage 
                  managerData={managerData} 
                  kpiData={kpiData} 
                />
              } 
            />
            <Route 
              path="profile" 
              element={
                <ProfileSubPage 
                  formData={formData} 
                  setFormData={setFormData}
                  handleUpdateProfile={handleUpdateProfile}
                  updating={updating}
                />
              } 
            />
            <Route 
              path="security" 
              element={
                <SecuritySubPage 
                  passwordData={passwordData}
                  setPasswordData={setPasswordData}
                  handleChangePassword={handleChangePassword}
                  changingPassword={changingPassword}
                  showCurrentPasscode={showCurrentPasscode}
                  setShowCurrentPasscode={setShowCurrentPasscode}
                  showNewPasscode={showNewPasscode}
                  setShowNewPasscode={setShowNewPasscode}
                />
              } 
            />
            <Route path="*" element={<Navigate to="performance" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default ManagerDashboard;
